<script setup lang="ts">
import type { HTMLAttributes } from 'vue'
import { cn } from '@elveris/ui/lib/utils'

import { computed, ref } from 'vue'
import { Button } from '../button'
import { formatKit, formatKitFileSize, useKitLocale, useKitMessages } from '../i18n'
import { Icon } from '../icon'
import { Progress } from '../progress'
import { Surface } from '../surface'

interface FileUploadProps {
  accept?: string
  multiple?: boolean
  maxSize?: number // in bytes
  maxFiles?: number
  disabled?: boolean
  showProgress?: boolean
  /** Emit selected files without keeping local File objects or pending rows. */
  clearOnSelect?: boolean
  dragAndDrop?: boolean
  /**
   * Accepts every form Vue's `class` binding takes — string, object, array.
   *
   * Was `string`, which made the ordinary conditional binding
   * `:class="{ 'foo': cond }"` a type error at consumers even though it is valid
   * Vue and `cn` (tailwind-merge) already accepted it at runtime. 229 of the 287
   * components in this package were already typed this way; these 58 were the
   * inconsistent minority (swept 2026-09-27, measured: no component reads
   * `props.class` as a string).
   */
  class?: HTMLAttributes['class']
  /**
   * Real uploader injected by the host. Receives each validated file and a
   * progress callback (0–100); resolve = success, reject = failure. When
   * OMITTED the component is purely presentational — it emits `files-selected`
   * and leaves the rows in `pending`; it NEVER fabricates progress or a
   * success it can't verify (the previous setInterval "simulation" lied that
   * files had uploaded when nothing left the browser).
   */
  uploadFn?: (file: File, onProgress: (percent: number) => void) => Promise<void>
  removeLabel?: string
  /** File-picker button text. English default, pass `t(...)` to localize. */
  chooseFilesLabel?: string
}

interface UploadedFile {
  file: File
  id: string
  progress: number
  status: 'pending' | 'uploading' | 'success' | 'error'
  error?: string
}

const props = withDefaults(defineProps<FileUploadProps>(), {
  accept: '*/*',
  multiple: false,
  maxSize: 10 * 1024 * 1024, // 10MB
  maxFiles: 5,
  disabled: false,
  showProgress: true,
  clearOnSelect: false,
  dragAndDrop: true,
})

const emit = defineEmits<{
  'files-selected': [files: File[]]
  'file-uploaded': [file: UploadedFile]
  'upload-complete': [files: UploadedFile[]]
  error: [error: string]
}>()
const messages = useKitMessages()
const kitLocale = useKitLocale()
const removeText = computed(() => props.removeLabel ?? messages.value.fileField.remove)
const chooseFilesText = computed(() => props.chooseFilesLabel ?? messages.value.fileUpload.chooseFiles)

const fileInputRef = ref<HTMLInputElement>()
const isDragging = ref(false)
const uploadedFiles = ref<UploadedFile[]>([])

// Computed properties
const acceptedFileTypes = computed(() => props.accept)
const canAddMoreFiles = computed(() => uploadedFiles.value.length < props.maxFiles)
// P11-a11y-keyboard (2026-09-12): single source of truth for "can the picker
// be operated right now" — drives the click guard, the tab order and the
// aria-disabled state so they cannot drift apart.
const pickerEnabled = computed(() => !props.disabled && canAddMoreFiles.value)

// File validation
function validateFile(file: File): string | null {
  if (file.size > props.maxSize) {
    return formatKit(messages.value.fileUpload.tooLarge, {
      size: (file.size / 1024 / 1024).toFixed(2),
      max: (props.maxSize / 1024 / 1024).toFixed(2),
    })
  }

  if (props.accept !== '*/*' && props.accept !== '*') {
    const acceptedTypes = props.accept.split(',').map((type) => type.trim())
    const isAccepted = acceptedTypes.some((type) => {
      if (type.startsWith('.')) {
        return file.name.toLowerCase().endsWith(type.toLowerCase())
      }
      return file.type.match(type.replace('*', '.*'))
    })

    if (!isAccepted) {
      return formatKit(messages.value.fileUpload.typeNotAccepted, { type: file.type })
    }
  }

  return null
}

// File handling
function handleFiles(files: FileList) {
  const fileArray = Array.from(files)
  const validFiles: File[] = []
  const errors: string[] = []

  if (!props.multiple && fileArray.length > 1) {
    errors.push(messages.value.fileUpload.singleOnly)
    emit('error', messages.value.fileUpload.singleOnly)
    return
  }

  const selectionLimit = props.clearOnSelect ? fileArray.length : uploadedFiles.value.length + fileArray.length
  if (selectionLimit > props.maxFiles || !canAddMoreFiles.value) {
    const tooMany = formatKit(messages.value.fileUpload.tooMany, { max: props.maxFiles })
    errors.push(tooMany)
    emit('error', tooMany)
    return
  }

  fileArray.forEach((file) => {
    const error = validateFile(file)
    if (error) {
      errors.push(`${file.name}: ${error}`)
    } else {
      validFiles.push(file)
    }
  })

  if (errors.length > 0) {
    emit('error', errors.join('; '))
  }

  if (validFiles.length > 0) {
    emit('files-selected', validFiles)
    if (!props.clearOnSelect) uploadFiles(validFiles)
  }
}

// Real upload via the injected `uploadFn` — no fabricated progress/success.
async function uploadOne(
  uploadedFile: UploadedFile,
  fn: NonNullable<FileUploadProps['uploadFn']>,
): Promise<void> {
  uploadedFile.status = 'uploading'
  try {
    await fn(uploadedFile.file, (pct) => {
      uploadedFile.progress = Math.max(0, Math.min(100, Math.round(pct)))
    })
    uploadedFile.progress = 100
    uploadedFile.status = 'success'
    emit('file-uploaded', uploadedFile)
  } catch (e) {
    uploadedFile.status = 'error'
    uploadedFile.error = e instanceof Error ? e.message : String(e)
    emit('error', uploadedFile.error)
  } finally {
    // Fire upload-complete only once every row has actually settled.
    if (uploadedFiles.value.every((f) => f.status === 'success' || f.status === 'error')) {
      emit('upload-complete', uploadedFiles.value)
    }
  }
}

function uploadFiles(files: File[]) {
  for (const file of files) {
    const uploadedFile: UploadedFile = {
      file,
      id: typeof crypto !== 'undefined' && crypto.randomUUID
        ? crypto.randomUUID()
        : `${Date.now()}-${file.name}`,
      progress: 0,
      // No uploader wired → 'pending' (host uploads via `files-selected`); a
      // real uploader → 'uploading' then driven by uploadOne. We never invent
      // a 'success' the upload didn't actually achieve.
      status: props.uploadFn ? 'uploading' : 'pending',
    }
    uploadedFiles.value.push(uploadedFile)
    if (props.uploadFn) void uploadOne(uploadedFile, props.uploadFn)
  }
}

// Event handlers
function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement
  if (target.files) {
    handleFiles(target.files)
  }
  // Let users select the same file again after an error or a completed one-shot flow.
  target.value = ''
}

function onClickUpload() {
  if (pickerEnabled.value) fileInputRef.value?.click()
}

function onDragOver(event: DragEvent) {
  event.preventDefault()
  if (props.dragAndDrop && pickerEnabled.value) {
    isDragging.value = true
  }
}

function onDragLeave() {
  isDragging.value = false
}

function onDrop(event: DragEvent) {
  event.preventDefault()
  isDragging.value = false

  if (!pickerEnabled.value || !props.dragAndDrop) return

  const files = event.dataTransfer?.files
  if (files) {
    handleFiles(files)
  }
}

function removeFile(fileId: string) {
  uploadedFiles.value = uploadedFiles.value.filter((f) => f.id !== fileId)
}

function formatFileSize(bytes: number): string {
  return formatKitFileSize(bytes, kitLocale.value)
}
</script>

<template>
  <div :class="cn('w-full space-y-4', props.class)">
    <!-- Keep the native picker outside its semantic trigger to avoid nested controls. -->
    <input
      ref="fileInputRef"
      type="file"
      :accept="acceptedFileTypes"
      :multiple="multiple"
      :disabled="disabled || !canAddMoreFiles"
      class="hidden"
      tabindex="-1"
      aria-hidden="true"
      @change="onFileChange"
    >

    <!-- Upload area -->
    <Button
      v-if="props.dragAndDrop"
      data-slot="dropzone"
      type="button"
      variant="outline"
      :disabled="!pickerEnabled"
      :aria-label="chooseFilesText"
      :class="
        cn(
          'h-auto min-h-20 w-full whitespace-normal border-2 border-dashed rounded-lg p-6 text-center transition-colors',
          'hover:border-theme-brand/50 hover:bg-theme-brand/5',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-theme-brand focus-visible:ring-offset-2',
          isDragging ? 'border-theme-brand bg-theme-brand/10' : 'border-theme-border',
          !pickerEnabled && 'opacity-50 cursor-not-allowed',
        )
      "
      content-class="w-full flex-col items-center gap-2"
      @click="onClickUpload"
      @dragover="onDragOver"
      @dragleave="onDragLeave"
      @drop="onDrop"
    >
      <slot name="dropzone" :is-dragging="isDragging" :accept="accept" :max-size="maxSize" :multiple="multiple">
        <!-- Upload icon -->
        <div class="flex flex-col items-center space-y-2">
          <Icon
            :icon="isDragging ? 'mdi:cloud-upload' : 'mdi:cloud-upload-outline'"
            class="h-12 w-12 text-theme-muted-foreground"
            :class="{ 'animate-bounce': isDragging }"
            aria-hidden="true"
          />

          <div class="text-lg font-medium text-theme-fg">
            {{ isDragging ? messages.fileUpload.dropHere : messages.fileUpload.dropOrClick }}
          </div>

          <div class="text-sm text-theme-muted-foreground">
            <span v-if="accept !== '*/*'">{{ formatKit(messages.fileUploadExtra.accepts, { types: accept }) }}</span>
            <span v-if="accept !== '*/*' && maxSize > 0"> • </span>
            <span v-if="maxSize > 0">{{ formatKit(messages.fileUploadExtra.maxSize, { size: formatFileSize(maxSize) }) }}</span>
          </div>

          <div v-if="!multiple && maxFiles > 1" class="text-xs text-theme-muted-foreground">
            {{ formatKit(messages.fileUploadExtra.singleLimit, { max: maxFiles }) }}
          </div>

          <div v-else-if="multiple" class="text-xs text-theme-muted-foreground">
            {{ formatKit(messages.fileUploadExtra.multipleLimit, { max: maxFiles }) }}
          </div>
        </div>
      </slot>
    </Button>

    <!-- Upload button (alternative) -->
    <Button v-if="!props.dragAndDrop" variant="outline" :disabled="disabled || !canAddMoreFiles" class="w-full" @click="onClickUpload">
      <Icon name="upload" class="me-2 h-4 w-4" aria-hidden="true" />
      {{ chooseFilesText }}
    </Button>

    <!-- Uploaded files list -->
    <div v-if="uploadedFiles.length > 0" class="space-y-2">
      <div class="text-sm font-medium text-theme-fg">
        {{ formatKit(messages.fileUploadExtra.uploadedCount, { count: uploadedFiles.length, max: maxFiles }) }}
      </div>

      <div class="space-y-2">
        <Surface v-for="uploadedFile in uploadedFiles" :key="uploadedFile.id" :level="0" class="flex items-center rounded-lg p-3 space-x-3">
          <!-- File icon -->
          <Icon
            :icon="uploadedFile.status === 'success' ? 'mdi:file-check' : uploadedFile.status === 'error' ? 'mdi:file-alert' : 'mdi:file-upload'"
            :class="{
              'text-theme-success': uploadedFile.status === 'success',
              'text-theme-error': uploadedFile.status === 'error',
              'text-theme-muted-foreground': uploadedFile.status === 'uploading',
            }"
            class="h-5 w-5"
            aria-hidden="true"
          />

          <!-- File info -->
          <div class="min-w-0 flex-1">
            <div class="truncate text-sm font-medium text-theme-fg">
              {{ uploadedFile.file.name }}
            </div>
            <div class="text-xs text-theme-muted-foreground">
              {{ formatFileSize(uploadedFile.file.size) }}
              <span v-if="uploadedFile.error" class="ms-2 text-theme-error"> • {{ uploadedFile.error }} </span>
            </div>
          </div>

          <!-- Progress bar -->
          <div v-if="showProgress && uploadedFile.status === 'uploading'" class="max-w-xs flex-1">
            <Progress :value="uploadedFile.progress" class="h-2" />
            <div class="mt-1 text-xs text-theme-muted-foreground">
              {{ Math.round(uploadedFile.progress) }}%
            </div>
          </div>

          <!-- Remove button -->
          <Button variant="ghost" size="sm" class="h-8 w-8 p-0" :aria-label="removeText" @click="removeFile(uploadedFile.id)">
            <Icon name="close" class="h-4 w-4" aria-hidden="true" />
          </Button>
        </Surface>
      </div>
    </div>
  </div>
</template>
